import { useRef, useState, useEffect } from "react";
import { useTheme } from "@mui/material";
import { useStreamContext } from "~contexts/Stream";
import { useAuth } from "~contexts/Auth";
import { ROLES } from "~routes/constants";
import useClickOutside from "~hooks/useClickOutside";
import { authFetch } from "~helpers/authFetch";
import { CreateBrandModal } from "~components/atoms";
import type { ICurrentStream } from "~api/stream/interfaces";
import * as S from "./styles";

interface StreamFromDB {
  id_stream: number;
  brand: string;
  stream_name: string;
  sales_channel: number;
}

interface IBrandSelectorProps {
  placeholder?: string;
  showPrefix?: boolean;
  invertColors?: boolean;
}

const BrandSelector = ({
  placeholder = "selecione uma marca",
  showPrefix = false,
  invertColors = false
}: IBrandSelectorProps) => {
  const theme: any = useTheme();
  const selectorRef = useRef<HTMLDivElement>(null);
  const { currentStream, setCurrentStream } = useStreamContext();
  const { user, getAvailableStreams } = useAuth();
  const [isDropdownOpened, setDropdownOpened] = useState<boolean>(false);
  const [isAddBrandModalOpen, setIsAddBrandModalOpen] = useState<boolean>(false);
  const [streams, setStreams] = useState<StreamFromDB[]>([]);
  const [loadingStreams, setLoadingStreams] = useState<boolean>(true);

  // Buscar streams do backend
  useEffect(() => {
    const loadStreams = async () => {
      try {
        setLoadingStreams(true);
        const response = await authFetch({
          path: `${process.env.REACT_APP_BACKEND_URL}/users/streams/list`,
          method: "GET",
        });

        if (response.ok) {
          const data = await response.json();
          setStreams(data);
        }
      } catch (error) {
        console.error("Error loading streams:", error);
      } finally {
        setLoadingStreams(false);
      }
    };

    if (user) {
      loadStreams();
    }
  }, [user]);

  // Filtrar streams baseado nas streams disponíveis para o usuário
  const availableStreamsFromPermissions = user ? getAvailableStreams() : [];
  const availableStreamIds = availableStreamsFromPermissions.map(s => s.id_stream);
  const availableStreams =
    user?.role === "admin"
      ? streams
      : streams.filter((stream) => availableStreamIds.includes(stream.id_stream));

  const displayText = currentStream
    ? showPrefix
      ? `operador ${currentStream.brand.toUpperCase()}`
      : currentStream.brand.toUpperCase()
    : placeholder;

  const onSelectStream = (stream: StreamFromDB, e: React.MouseEvent) => {
    e.stopPropagation();
    // Converter stream do banco para formato ICurrentStream
    const streamToSet: ICurrentStream = {
      id: stream.id_stream,
      name: stream.stream_name,
      label: stream.brand,
      brand: stream.brand,
      sales_channel: String(stream.sales_channel || ""),
    };
    setCurrentStream(streamToSet);
    setDropdownOpened(false);
  };

  const handleAddBrandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsAddBrandModalOpen(true);
    setDropdownOpened(false);
  };

  const handleCloseAddBrandModal = () => {
    setIsAddBrandModalOpen(false);
  };

  const handleBrandCreated = async () => {
    // Recarregar lista de streams após criar marca
    try {
      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/users/streams/list`,
        method: "GET",
      });

      if (response.ok) {
        const data = await response.json();
        setStreams(data);
      }
    } catch (error) {
      console.error("Error reloading streams:", error);
    }
  };

  useClickOutside(selectorRef, () => isDropdownOpened && setDropdownOpened(false));

  return (
    <S.Wrapper ref={selectorRef} theme={theme}>
      <S.SelectorButton
        onClick={() => setDropdownOpened(!isDropdownOpened)}
        theme={theme}
        $invertColors={invertColors}
      >
        <S.SelectorText>{displayText}</S.SelectorText>
        <S.Icon />
      </S.SelectorButton>
      <S.Dropdown theme={theme} $isOpened={isDropdownOpened}>
        {loadingStreams ? (
          <S.StreamLabel $isSelected={false}>Carregando...</S.StreamLabel>
        ) : (
          <>
            {availableStreams.map((stream) => {
              const isSelected = currentStream?.brand === stream.brand &&
                currentStream?.name === stream.stream_name;
              return (
                <S.StreamLabel
                  $isSelected={isSelected}
                  key={stream.id_stream}
                  onClick={(e: React.MouseEvent) => onSelectStream(stream, e)}
                >
                  <S.StreamBrandName>
                    {showPrefix ? `operador ${stream.brand.toUpperCase()}` : stream.brand.toUpperCase()}
                  </S.StreamBrandName>
                  <S.StreamName>{stream.stream_name}</S.StreamName>
                </S.StreamLabel>
              );
            })}
            {user?.role === ROLES.admin && (
              <S.AddBrandLabel
                onClick={handleAddBrandClick}
                theme={theme}
              >
                adicionar marca +
              </S.AddBrandLabel>
            )}
          </>
        )}
      </S.Dropdown>

      {/* Modal de Adicionar Marca */}
      <CreateBrandModal
        isOpened={isAddBrandModalOpen}
        onClose={handleCloseAddBrandModal}
        onSuccess={handleBrandCreated}
      />
    </S.Wrapper>
  );
};

export default BrandSelector;
